import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faReceipt,
    faSpinner,
    faEdit,
    faTrash,
    faEye,
    faCheck,
    faBan,
    faMoneyBillWave,
    faFilter,
    faSearch,
    faTimes,
    faChevronLeft,
    faChevronRight,
    faDownload,
    faPrint,
    faBuilding,
    faCreditCard,
    faMoneyCheck,
    faMobile,
    faCalendarAlt,
    faTag,
    faChartLine,
    faFileInvoice,
    faStore,
    faUser,
    faPhone,
    faPercentage
} from '@fortawesome/free-solid-svg-icons';

const PosExpensesList = ({ user, currentShop, onEdit, onRefresh }) => {
    const [shopId, setShopId] = useState(() => {
        if (user?.shop_id) return user.shop_id;
        return localStorage.getItem('current_shop_id') || '';
    });

    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState(null);
    const [filters, setFilters] = useState({
        date_from: '',
        date_to: '',
        category: '',
        status: '',
        payment_method: '',
        search: ''
    });
    const [showFilters, setShowFilters] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0
    });
    const [categories, setCategories] = useState([]);
    const [selectedExpenses, setSelectedExpenses] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    // Load categories
    const loadCategories = useCallback(async () => {
        try {
            const response = await fetch('https://yourdomain.com/backend/pos_expenses.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_categories',
                    shop_id: shopId
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setCategories(data.data);
            }
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    }, [shopId]);

    // Load expenses
    const loadExpenses = useCallback(async () => {
        if (!shopId) return;
        
        setLoading(true);
        try {
            const payload = {
                action: 'get_expenses',
                shop_id: shopId,
                page: pagination.page,
                limit: pagination.limit,
                sort: 'expense_date',
                order: 'DESC'
            };
            
            if (filters.date_from) payload.filters = { ...payload.filters, date_from: filters.date_from };
            if (filters.date_to) payload.filters = { ...payload.filters, date_to: filters.date_to };
            if (filters.category) payload.filters = { ...payload.filters, category: filters.category };
            if (filters.status) payload.filters = { ...payload.filters, status: filters.status };
            if (filters.payment_method) payload.filters = { ...payload.filters, payment_method: filters.payment_method };
            if (filters.search) payload.filters = { ...payload.filters, search: filters.search };
            
            const response = await fetch('https://yourdomain.com/backend/pos_expenses.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (data.status === 'success') {
                setExpenses(data.data);
                setPagination(prev => ({ ...prev, total: data.total }));
                setSummary(data.summary);
            }
        } catch (error) {
            console.error('Error loading expenses:', error);
        } finally {
            setLoading(false);
        }
    }, [shopId, pagination.page, pagination.limit, filters]);

    // Delete expense
    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this expense?')) return;
        
        try {
            const response = await fetch('https://yourdomain.com/backend/pos_expenses.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'delete_expense',
                    id: id
                })
            });
            
            const data = await response.json();
            if (data.status === 'success') {
                loadExpenses();
                if (onRefresh) onRefresh();
            }
        } catch (error) {
            console.error('Error deleting expense:', error);
        }
    };

    // Bulk delete
    const handleBulkDelete = async () => {
        if (selectedExpenses.length === 0) return;
        if (!window.confirm(`Are you sure you want to delete ${selectedExpenses.length} expense(s)?`)) return;
        
        for (const id of selectedExpenses) {
            try {
                await fetch('https://yourdomain.com/backend/pos_expenses.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'delete_expense',
                        id: id
                    })
                });
            } catch (error) {
                console.error(`Error deleting expense ${id}:`, error);
            }
        }
        
        setSelectedExpenses([]);
        setSelectAll(false);
        loadExpenses();
        if (onRefresh) onRefresh();
    };

    // Update status
    const handleStatusUpdate = async (id, status) => {
        try {
            const response = await fetch('https://yourdomain.com/backend/pos_expenses.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'update_status',
                    id: id,
                    status: status,
                    approved_by: user?.username
                })
            });
            
            const data = await response.json();
            if (data.status === 'success') {
                loadExpenses();
                if (onRefresh) onRefresh();
            }
        } catch (error) {
            console.error('Error updating status:', error);
        }
    };

    // Handle select all
    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedExpenses([]);
        } else {
            setSelectedExpenses(expenses.map(exp => exp.id));
        }
        setSelectAll(!selectAll);
    };

    // Handle select single
    const handleSelectSingle = (id) => {
        if (selectedExpenses.includes(id)) {
            setSelectedExpenses(selectedExpenses.filter(expId => expId !== id));
        } else {
            setSelectedExpenses([...selectedExpenses, id]);
        }
    };

    // Export to CSV
    const exportToCSV = () => {
        const headers = ['Expense No', 'Date', 'Title', 'Category', 'Amount', 'Tax', 'Total', 'Payment Method', 'Status', 'Vendor', 'Created By'];
        const csvData = expenses.map(exp => [
            exp.expense_no,
            exp.expense_date,
            `"${exp.title.replace(/"/g, '""')}"`,
            exp.category,
            exp.amount,
            exp.tax,
            exp.total_amount,
            exp.payment_method,
            exp.status,
            exp.vendor_name || '',
            exp.created_by
        ]);
        
        const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `expenses_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // Print report
    const printReport = () => {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Expenses Report</title>
                    <style>
                        body { font-family: Arial, sans-serif; padding: 20px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                        th { background: #f5f5f5; }
                        .header { text-align: center; margin-bottom: 20px; }
                        .summary { margin-bottom: 20px; }
                        .total { font-weight: bold; margin-top: 20px; text-align: right; }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h2>Expenses Report</h2>
                        <p>Generated on: ${new Date().toLocaleString()}</p>
                        <p>Shop: ${currentShop?.shop_name || 'All Shops'}</p>
                    </div>
                    <div class="summary">
                        <p><strong>Total Expenses:</strong> ${summary?.total_expenses || 0}</p>
                        <p><strong>Total Amount:</strong> ₹${(summary?.total_amount || 0).toFixed(2)}</p>
                        <p><strong>Total Tax:</strong> ₹${(summary?.total_tax || 0).toFixed(2)}</p>
                    </div>
                    <table>
                        <thead>
                            <tr><th>Expense No</th><th>Date</th><th>Title</th><th>Category</th><th>Amount</th><th>Tax</th><th>Total</th><th>Status</th></tr>
                        </thead>
                        <tbody>
                            ${expenses.map(exp => `
                                <tr>
                                    <td>${exp.expense_no}</td>
                                    <td>${exp.expense_date}</td>
                                    <td>${exp.title}</td>
                                    <td>${exp.category}</td>
                                    <td>₹${exp.amount.toFixed(2)}</td>
                                    <td>₹${exp.tax.toFixed(2)}</td>
                                    <td>₹${exp.total_amount.toFixed(2)}</td>
                                    <td>${exp.status}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                    <div class="total">
                        <h3>Grand Total: ₹${(summary?.total_amount || 0).toFixed(2)}</h3>
                    </div>
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN');
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount || 0);
    };

    // Get status badge
    const getStatusBadge = (status) => {
        const colors = {
            pending: { bg: '#fef3c7', color: '#d97706', text: 'Pending' },
            approved: { bg: '#d1fae5', color: '#059669', text: 'Approved' },
            rejected: { bg: '#fee2e2', color: '#dc2626', text: 'Rejected' },
            paid: { bg: '#e0e7ff', color: '#4f46e5', text: 'Paid' }
        };
        const style = colors[status] || colors.pending;
        return (
            <span style={{
                padding: '4px 10px',
                borderRadius: '20px',
                fontSize: '11px',
                fontWeight: '500',
                background: style.bg,
                color: style.color,
                display: 'inline-block'
            }}>
                {style.text}
            </span>
        );
    };

    // Get payment method icon
    const getPaymentIcon = (method) => {
        const icons = {
            cash: faMoneyBillWave,
            bank_transfer: faBuilding,
            credit_card: faCreditCard,
            debit_card: faCreditCard,
            cheque: faMoneyCheck,
            upi: faMobile
        };
        return icons[method] || faMoneyBillWave;
    };

    useEffect(() => {
        if (shopId) {
            loadCategories();
            loadExpenses();
        }
    }, [shopId, loadCategories, loadExpenses]);

    useEffect(() => {
        if (currentShop) {
            setShopId(currentShop.shop_id);
        }
    }, [currentShop]);

    const paymentMethods = {
        cash: 'Cash',
        bank_transfer: 'Bank Transfer',
        credit_card: 'Credit Card',
        debit_card: 'Debit Card',
        cheque: 'Cheque',
        upi: 'UPI'
    };

    const statusOptions = [
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'paid', label: 'Paid' }
    ];

    return (
        <div className="pos-expenses-list">
            {/* Header with Summary */}
            <div className="list-header">
                <div className="header-left">
                    <h2>
                        <FontAwesomeIcon icon={faReceipt} />
                        Expense Records
                    </h2>
                    {summary && (
                        <div className="summary-badge">
                            <span>Total: ₹{formatCurrency(summary.total_amount)}</span>
                            <span>| {summary.total_expenses} Expenses</span>
                        </div>
                    )}
                </div>
                <div className="header-actions">
                    <button onClick={exportToCSV} className="action-icon-btn" title="Export CSV">
                        <FontAwesomeIcon icon={faDownload} />
                    </button>
                    <button onClick={printReport} className="action-icon-btn" title="Print">
                        <FontAwesomeIcon icon={faPrint} />
                    </button>
                    <button onClick={() => setShowFilters(!showFilters)} className={`filter-btn ${showFilters ? 'active' : ''}`}>
                        <FontAwesomeIcon icon={faFilter} />
                        Filters
                    </button>
                </div>
            </div>

            {/* Filters Panel */}
            {showFilters && (
                <div className="filters-panel">
                    <div className="filter-row">
                        <div className="filter-group">
                            <label>Date From</label>
                            <input
                                type="date"
                                value={filters.date_from}
                                onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                            />
                        </div>
                        <div className="filter-group">
                            <label>Date To</label>
                            <input
                                type="date"
                                value={filters.date_to}
                                onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                            />
                        </div>
                        <div className="filter-group">
                            <label>Category</label>
                            <select
                                value={filters.category}
                                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                            >
                                <option value="">All Categories</option>
                                {categories.map(cat => (
                                    <option key={cat.id} value={cat.category_name}>{cat.category_name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="filter-group">
                            <label>Status</label>
                            <select
                                value={filters.status}
                                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                            >
                                <option value="">All Status</option>
                                {statusOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="filter-group">
                            <label>Payment Method</label>
                            <select
                                value={filters.payment_method}
                                onChange={(e) => setFilters(prev => ({ ...prev, payment_method: e.target.value }))}
                            >
                                <option value="">All Methods</option>
                                {Object.entries(paymentMethods).map(([value, label]) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="search-row">
                        <div className="search-box">
                            <FontAwesomeIcon icon={faSearch} />
                            <input
                                type="text"
                                placeholder="Search by expense no, title, vendor..."
                                value={filters.search}
                                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                onKeyPress={(e) => e.key === 'Enter' && loadExpenses()}
                            />
                        </div>
                        <div className="filter-actions">
                            <button onClick={() => { setPagination(prev => ({ ...prev, page: 1 })); loadExpenses(); }} className="apply-btn">
                                Apply
                            </button>
                            <button onClick={() => {
                                setFilters({ date_from: '', date_to: '', category: '', status: '', payment_method: '', search: '' });
                                setPagination(prev => ({ ...prev, page: 1 }));
                                loadExpenses();
                            }} className="reset-btn">
                                Reset
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Actions */}
            {selectedExpenses.length > 0 && (
                <div className="bulk-actions">
                    <span>{selectedExpenses.length} item(s) selected</span>
                    <button onClick={handleBulkDelete} className="bulk-delete">
                        <FontAwesomeIcon icon={faTrash} />
                        Delete Selected
                    </button>
                </div>
            )}

            {/* Expenses Table */}
            {loading ? (
                <div className="loading-state">
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <p>Loading expenses...</p>
                </div>
            ) : expenses.length === 0 ? (
                <div className="empty-state">
                    <FontAwesomeIcon icon={faReceipt} />
                    <p>No expenses found</p>
                    <p className="empty-hint">Try adjusting your filters or add a new expense</p>
                </div>
            ) : (
                <>
                    <div className="table-wrapper">
                        <table className="expenses-table">
                            <thead>
                                <tr>
                                    <th className="checkbox-col">
                                        <input
                                            type="checkbox"
                                            checked={selectAll}
                                            onChange={handleSelectAll}
                                        />
                                    </th>
                                    <th>Expense No</th>
                                    <th>Date</th>
                                    <th>Title</th>
                                    <th>Category</th>
                                    <th>Amount</th>
                                    <th>Tax</th>
                                    <th>Total</th>
                                    <th>Payment</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {expenses.map(expense => (
                                    <tr key={expense.id} className={selectedExpenses.includes(expense.id) ? 'selected' : ''}>
                                        <td className="checkbox-col">
                                            <input
                                                type="checkbox"
                                                checked={selectedExpenses.includes(expense.id)}
                                                onChange={() => handleSelectSingle(expense.id)}
                                            />
                                        </td>
                                        <td className="expense-no">{expense.expense_no}</td>
                                        <td>{formatDate(expense.expense_date)}</td>
                                        <td className="title-cell">
                                            <div className="title-info">
                                                <strong>{expense.title}</strong>
                                                {expense.vendor_name && (
                                                    <small>
                                                        <FontAwesomeIcon icon={faStore} size="xs" />
                                                        {expense.vendor_name}
                                                    </small>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="category-badge">{expense.category}</span>
                                        </td>
                                        <td>₹{formatCurrency(expense.amount)}</td>
                                        <td className="tax-cell">₹{formatCurrency(expense.tax)}</td>
                                        <td className="total-cell">₹{formatCurrency(expense.total_amount)}</td>
                                        <td>
                                            <span className="payment-badge">
                                                <FontAwesomeIcon icon={getPaymentIcon(expense.payment_method)} size="xs" />
                                                {paymentMethods[expense.payment_method] || expense.payment_method}
                                            </span>
                                        </td>
                                        <td>{getStatusBadge(expense.status)}</td>
                                        <td className="actions-cell">
                                            <button onClick={() => onEdit?.(expense)} className="icon-btn edit" title="Edit">
                                                <FontAwesomeIcon icon={faEdit} />
                                            </button>
                                            <button onClick={() => handleDelete(expense.id)} className="icon-btn delete" title="Delete">
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                            {expense.status === 'pending' && (
                                                <>
                                                    <button onClick={() => handleStatusUpdate(expense.id, 'approved')} className="icon-btn approve" title="Approve">
                                                        <FontAwesomeIcon icon={faCheck} />
                                                    </button>
                                                    <button onClick={() => handleStatusUpdate(expense.id, 'rejected')} className="icon-btn reject" title="Reject">
                                                        <FontAwesomeIcon icon={faBan} />
                                                    </button>
                                                </>
                                            )}
                                            {expense.status === 'approved' && (
                                                <button onClick={() => handleStatusUpdate(expense.id, 'paid')} className="icon-btn paid" title="Mark as Paid">
                                                    <FontAwesomeIcon icon={faMoneyBillWave} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {pagination.total > pagination.limit && (
                        <div className="pagination">
                            <button
                                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                disabled={pagination.page === 1}
                            >
                                <FontAwesomeIcon icon={faChevronLeft} />
                                Previous
                            </button>
                            <span className="page-info">
                                Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}
                            </span>
                            <button
                                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                            >
                                Next
                                <FontAwesomeIcon icon={faChevronRight} />
                            </button>
                        </div>
                    )}
                </>
            )}

            <style>{`
                .pos-expenses-list {
                    background: white;
                    border-radius: 16px;
                    padding: 20px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                }
                
                .list-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                    flex-wrap: wrap;
                    gap: 15px;
                }
                
                .header-left {
                    display: flex;
                    align-items: center;
                    gap: 15px;
                }
                
                .header-left h2 {
                    margin: 0;
                    font-size: 20px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                
                .summary-badge {
                    padding: 6px 12px;
                    background: #f1f5f9;
                    border-radius: 20px;
                    font-size: 13px;
                    color: #475569;
                }
                
                .header-actions {
                    display: flex;
                    gap: 10px;
                }
                
                .action-icon-btn {
                    width: 36px;
                    height: 36px;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    background: white;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s;
                }
                
                .action-icon-btn:hover {
                    background: #f8fafc;
                    border-color: #4f46e5;
                    color: #4f46e5;
                }
                
                .filter-btn {
                    padding: 0 16px;
                    height: 36px;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    background: white;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                
                .filter-btn.active {
                    background: #4f46e5;
                    color: white;
                    border-color: #4f46e5;
                }
                
                .filters-panel {
                    background: #f8fafc;
                    border-radius: 12px;
                    padding: 20px;
                    margin-bottom: 20px;
                }
                
                .filter-row {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
                    gap: 15px;
                    margin-bottom: 15px;
                }
                
                .filter-group label {
                    display: block;
                    font-size: 12px;
                    color: #64748b;
                    margin-bottom: 5px;
                }
                
                .filter-group input,
                .filter-group select {
                    width: 100%;
                    padding: 8px 12px;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    font-size: 13px;
                }
                
                .search-row {
                    display: flex;
                    gap: 15px;
                    align-items: center;
                }
                
                .search-box {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 8px 12px;
                    gap: 8px;
                }
                
                .search-box input {
                    flex: 1;
                    border: none;
                    outline: none;
                }
                
                .filter-actions {
                    display: flex;
                    gap: 10px;
                }
                
                .apply-btn, .reset-btn {
                    padding: 8px 20px;
                    border-radius: 8px;
                    cursor: pointer;
                }
                
                .apply-btn {
                    background: #4f46e5;
                    color: white;
                    border: none;
                }
                
                .reset-btn {
                    background: white;
                    border: 1px solid #e2e8f0;
                }
                
                .bulk-actions {
                    background: #e0e7ff;
                    border-radius: 8px;
                    padding: 10px 15px;
                    margin-bottom: 15px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                
                .bulk-delete {
                    background: #dc2626;
                    color: white;
                    border: none;
                    padding: 6px 12px;
                    border-radius: 6px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }
                
                .table-wrapper {
                    overflow-x: auto;
                }
                
                .expenses-table {
                    width: 100%;
                    border-collapse: collapse;
                }
                
                .expenses-table th,
                .expenses-table td {
                    padding: 12px;
                    text-align: left;
                    border-bottom: 1px solid #e2e8f0;
                }
                
                .expenses-table th {
                    background: #f8fafc;
                    font-weight: 600;
                    color: #475569;
                    font-size: 13px;
                }
                
                .expenses-table tr:hover {
                    background: #f8fafc;
                }
                
                .expenses-table tr.selected {
                    background: #e0e7ff;
                }
                
                .checkbox-col {
                    width: 40px;
                    text-align: center;
                }
                
                .expense-no {
                    font-family: monospace;
                    font-size: 12px;
                    color: #4f46e5;
                }
                
                .title-cell .title-info {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }
                
                .title-cell small {
                    font-size: 11px;
                    color: #64748b;
                    display: flex;
                    align-items: center;
                    gap: 4px;
                }
                
                .category-badge {
                    padding: 4px 8px;
                    background: #f1f5f9;
                    border-radius: 4px;
                    font-size: 11px;
                }
                
                .tax-cell {
                    color: #64748b;
                }
                
                .total-cell {
                    font-weight: 600;
                    color: #1e293b;
                }
                
                .payment-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 8px;
                    background: #f1f5f9;
                    border-radius: 4px;
                    font-size: 11px;
                }
                
                .actions-cell {
                    display: flex;
                    gap: 5px;
                    flex-wrap: wrap;
                }
                
                .icon-btn {
                    width: 30px;
                    height: 30px;
                    border-radius: 6px;
                    border: none;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                }
                
                .icon-btn.edit { background: #e0e7ff; color: #4f46e5; }
                .icon-btn.delete { background: #fee2e2; color: #dc2626; }
                .icon-btn.approve { background: #d1fae5; color: #10b981; }
                .icon-btn.reject { background: #fee2e2; color: #dc2626; }
                .icon-btn.paid { background: #d1fae5; color: #10b981; }
                
                .pagination {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 15px;
                    margin-top: 20px;
                }
                
                .pagination button {
                    padding: 8px 16px;
                    border: 1px solid #e2e8f0;
                    background: white;
                    border-radius: 6px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 5px;
                }
                
                .pagination button:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
                
                .page-info {
                    font-size: 14px;
                    color: #475569;
                }
                
                .loading-state, .empty-state {
                    text-align: center;
                    padding: 60px;
                    color: #64748b;
                }
                
                .loading-state svg {
                    font-size: 40px;
                    margin-bottom: 15px;
                }
                
                .empty-state svg {
                    font-size: 60px;
                    margin-bottom: 15px;
                    color: #cbd5e1;
                }
                
                .empty-hint {
                    font-size: 12px;
                    margin-top: 10px;
                }
                
                @media (max-width: 768px) {
                    .list-header {
                        flex-direction: column;
                        align-items: flex-start;
                    }
                    
                    .filter-row {
                        grid-template-columns: 1fr;
                    }
                    
                    .search-row {
                        flex-direction: column;
                    }
                    
                    .actions-cell {
                        flex-wrap: wrap;
                    }
                }
            `}</style>
        </div>
    );
};

export default PosExpensesList;